import React, { Suspense, useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useCompany } from './context/CompanyContext';
import { SettingsProvider } from './context/SettingsContext';
import { CompanyProvider } from './context/CompanyContext';
import { db } from './api/db';

import Layout from './components/layout/Layout';
import Home from './pages/Home';

const BlogList = React.lazy(() => import('./pages/BlogList'));
const BlogPost = React.lazy(() => import('./pages/BlogPost'));
const PageView = React.lazy(() => import('./pages/PageView'));
const Links = React.lazy(() => import('./pages/Links'));
const Login = React.lazy(() => import('./pages/Login'));
const Dashboard = React.lazy(() => import('./pages/admin/Dashboard'));
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const MenuManager = React.lazy(() => import('./pages/admin/MenuManager'));
const Pages = React.lazy(() => import('./pages/admin/Pages'));
const PageEditor = React.lazy(() => import('./pages/admin/PageEditor'));
const Media = React.lazy(() => import('./pages/admin/Media'));
const ArticleEditor = React.lazy(() => import('./pages/admin/ArticleEditor'));
const ManageUsers = React.lazy(() => import('./pages/admin/ManageUsers'));
const Domains = React.lazy(() => import('./pages/admin/Domains'));
const SiteSettings = React.lazy(() => import('./pages/admin/SiteSettings'));
const AccountSettings = React.lazy(() => import('./pages/admin/AccountSettings'));
const Activate = React.lazy(() => import('./pages/Activate'));
const Onboarding = React.lazy(() => import('./pages/Onboarding'));
const SignUp = React.lazy(() => import('./pages/SignUp'));
const SetupAccount = React.lazy(() => import('./pages/SetupAccount'));
const ResetRequest = React.lazy(() => import('./pages/ResetRequest'));
const VerifyDevice = React.lazy(() => import('./pages/VerifyDevice'));
const Forum = React.lazy(() => import('./pages/Forum'));
const ForumTopic = React.lazy(() => import('./pages/ForumTopic'));
const ForumNewTopic = React.lazy(() => import('./pages/ForumNewTopic'));
const WorkspaceHome = React.lazy(() => import('./pages/WorkspaceHome'));

const PageLoading = () => (
  <div className="p-10 text-center text-gray-500">Loading...</div>
);

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <PageLoading />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return children;
};

const CompanyAdmin = () => {
  const { isSuperuser, user, loading } = useAuth();
  const { company } = useCompany();

  if (loading) return <PageLoading />;
  if (!company) return <div className="p-10 text-center">No workspace selected.</div>;
  if (!isSuperuser && user?.blog_id !== company.id) {
    return <div className="p-10 text-center text-red-600">You do not have access to this workspace.</div>;
  }

  return (
    <Routes>
      <Route index element={<AdminDashboard />} />
      <Route path="menu" element={<MenuManager />} />
      <Route path="articles" element={<Dashboard />} />
      <Route path="pages" element={<Pages />} />
      <Route path="page-editor/:id" element={<PageEditor />} />
      <Route path="media" element={<Media />} />
      <Route path="editor/:id" element={<ArticleEditor />} />
      <Route path="users" element={<ManageUsers />} />
      <Route path="account" element={<AccountSettings />} />
      <Route path="domains" element={<Domains />} />
      <Route path="settings" element={<SiteSettings />} />
      <Route path="onboarding" element={<Onboarding />} />
    </Routes>
  );
};

const AppRoutes = () => {
  useEffect(() => {
    // Initialize mock database
    db.init();
  }, []);

  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="links" element={<Links />} />
          <Route path="blog" element={<BlogList />} />
          <Route path="blog/:slug" element={<BlogPost />} />
          <Route path="page/:slug" element={<PageView />} />
          <Route path="login" element={<Login />} />

          {/* Protected Admin Routes */}
          <Route path="admin" element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="admin/menu" element={
            <ProtectedRoute>
              <MenuManager />
            </ProtectedRoute>
          } />
          <Route path="admin/articles" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="admin/pages" element={
            <ProtectedRoute>
              <Pages />
            </ProtectedRoute>
          } />
          <Route path="admin/page-editor/:id" element={
            <ProtectedRoute>
              <PageEditor />
            </ProtectedRoute>
          } />
          <Route path="admin/media" element={
            <ProtectedRoute>
              <Media />
            </ProtectedRoute>
          } />
          <Route path="admin/editor/:id" element={
            <ProtectedRoute>
              <ArticleEditor />
            </ProtectedRoute>
          } />
          <Route path="admin/users" element={
            <ProtectedRoute>
              <ManageUsers />
            </ProtectedRoute>
          } />
          <Route path="admin/account" element={
            <ProtectedRoute>
              <AccountSettings />
            </ProtectedRoute>
          } />
          <Route path="admin/domains" element={
            <ProtectedRoute>
              <Domains />
            </ProtectedRoute>
          } />
          <Route path="activate" element={<Activate />} />
          <Route path="signup" element={<SignUp />} />
          <Route path="setup-account" element={<SetupAccount />} />
          <Route path="reset-request" element={<ResetRequest />} />
          <Route path="verify-device" element={<VerifyDevice />} />
          <Route path="forum" element={<Forum />} />
          <Route path="forum/new" element={
            <ProtectedRoute>
              <ForumNewTopic />
            </ProtectedRoute>
          } />
          <Route path="forum/:id" element={<ForumTopic />} />
          <Route path="onboarding" element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          } />
          <Route path="admin/settings" element={
            <ProtectedRoute>
              <SiteSettings />
            </ProtectedRoute>
          } />
          <Route path="community" element={<Forum />} />
          <Route path="community/new" element={
            <ProtectedRoute>
              <ForumNewTopic />
            </ProtectedRoute>
          } />
          <Route path="community/:id" element={<ForumTopic />} />
          <Route path=":company" element={<WorkspaceHome />} />
          <Route path=":company/blog" element={<BlogList />} />
          <Route path=":company/blog/:slug" element={<BlogPost />} />
          <Route path=":company/links" element={<Links />} />
          <Route path=":company/forum" element={<Forum />} />
          <Route path=":company/forum/new" element={
            <ProtectedRoute>
              <ForumNewTopic />
            </ProtectedRoute>
          } />
          <Route path=":company/forum/:id" element={<ForumTopic />} />
          <Route path=":company/community" element={<Forum />} />
          <Route path=":company/community/new" element={
            <ProtectedRoute>
              <ForumNewTopic />
            </ProtectedRoute>
          } />
          <Route path=":company/community/:id" element={<ForumTopic />} />
          <Route path=":company/admin/*" element={
            <ProtectedRoute>
              <CompanyAdmin />
            </ProtectedRoute>
          } />
        </Route>
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <AuthProvider>
      <HashRouter>
        <CompanyProvider>
          <SettingsProvider>
            <AppRoutes />
          </SettingsProvider>
        </CompanyProvider>
      </HashRouter>
    </AuthProvider>
  );
}

export default App;
